import {useCallback, useEffect, useReducer, useState} from 'react';

import {
  Accordion,
  AmountInput,
  Button,
  Modal,
  SpinningLoader,
} from '@/components';
import {NumberInput} from '@/components/common/Input';
import type {
  AfriexAdminAmount,
  AfriexCurrency,
  AfriexCurrencyCode,
} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

import BusinessTransactionProcessorConfig from './BusinessTransactionProcessorConfig';
import CorridorKillSwitches from './CorridorKillSwitches';
import type {CurrencyFormAction, CurrencyFormState} from './currencyReducer';
import {
  currencyFormReducer,
  initialCurrencyFormState,
  prepareSubmissionData,
} from './currencyReducer';
import RetailTransactionLimitConfig from './RetailTransactionLimitConfig';

type UpdateFormModalProps = {
  onClose: VoidFunction;
  handleSave: (id: string, body: Partial<AfriexCurrency>) => Promise<void>;
  item: AfriexCurrency;
  showModal: boolean;
  externalState?: CurrencyFormState;
  externalDispatch?: React.Dispatch<CurrencyFormAction>;
};

const UpdateFormModal: React.FC<UpdateFormModalProps> = ({
  onClose,
  showModal,
  handleSave,
  item,
  externalState,
  externalDispatch,
}) => {
  const [localState, localDispatch] = useReducer(
    currencyFormReducer,
    initialCurrencyFormState,
  );
  const [isRetailLimitExpanded, setIsRetailLimitExpanded] = useState(false);
  const [isKillSwitchExpanded, setIsKillSwitchExpanded] = useState(false);
  const [isProcessorExpanded, setIsProcessorExpanded] = useState(false);

  // Use external state if provided, otherwise use local state
  const state = externalState ?? localState;
  const dispatch = externalDispatch ?? localDispatch;

  useEffect(() => {
    if (item) {
      dispatch({type: 'INITIALIZE_FORM', payload: item});
    }
  }, [item, dispatch]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const updateData = prepareSubmissionData(state, item);
      dispatch({type: 'SET_LOADING', payload: true});
      try {
        await handleSave(item?.id as string, updateData);
        // Delay modal close to show success feedback
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error) {
        showErrorMessage(
          getResponseMessage(error) || 'Failed to save currency settings',
        );
      } finally {
        dispatch({type: 'SET_LOADING', payload: false});
      }
    },
    [state, handleSave, item, dispatch, onClose],
  );

  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      width="w-5/6 md:w-2/5 lg:w-1/3 xl:w-4/12 2xl:w-3/12"
      height={
        isProcessorExpanded || isRetailLimitExpanded || isKillSwitchExpanded
          ? 'h-4/5 xl:h-3/12 2xl:h-3/5'
          : 'h-auto'
      }
      title="Currency Settings"
      closeOnOutsideClick
    >
      {state.isLoading ? (
        <div className="flex w-full h-full justify-center items-center">
          <SpinningLoader />
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {/* Scrollable Form Content */}
          <div className="flex-1 px-3">
            <form
              onSubmit={handleSubmit}
              id="currency-settings-form"
              className="flex flex-col space-y-5 pb-4"
            >
              <AmountInput
                value={{
                  amount: state.params?.referralPayoutRate || 0,
                  currency: item?.code as AfriexCurrencyCode,
                }}
                fixedCurrency={item?.code as AfriexCurrencyCode}
                label="Referral Payout Rate"
                min={1}
                max={999999}
                onChange={(value: AfriexAdminAmount) => {
                  dispatch({
                    type: 'UPDATE_PARAM',
                    payload: {
                      key: 'referralPayoutRate',
                      value: value?.amount || '',
                    },
                  });
                }}
              />

              <NumberInput
                id="withdrawalFeeBasisPoints"
                className="w-full h-[44px]"
                value={state.params?.withdrawalFeeBasisPoints || ''}
                min={0}
                max={10000}
                label="Withdrawal Fee Basis Points"
                onValueChange={(rawValue) => {
                  dispatch({
                    type: 'UPDATE_PARAM',
                    payload: {
                      key: 'withdrawalFeeBasisPoints',
                      value: Number(rawValue) || '',
                    },
                  });
                }}
              />

              {/* Retail Update Section */}
              <div className="mt-6">
                <h3 className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                  Retail Configuration
                </h3>

                <Accordion
                  title="Corridor Availability"
                  headerStyle="bg-gray-200 dark:bg-gray-800 shadow-sm py-1.5"
                  onClick={(isActive) => setIsKillSwitchExpanded(isActive)}
                >
                  <CorridorKillSwitches
                    permissions={state.permissions}
                    onToggle={(permission, allowed) =>
                      dispatch({
                        type: 'SET_CURRENCY_PERMISSION',
                        payload: {permission, allowed},
                      })
                    }
                  />
                </Accordion>

                <Accordion
                  title="Daily Count Limits"
                  headerStyle="bg-gray-200 dark:bg-gray-800 shadow-sm py-1.5"
                  onClick={(isActive) => setIsRetailLimitExpanded(isActive)}
                >
                  <RetailTransactionLimitConfig
                    user={state.user}
                    onSetLimit={(transactionType, limits) =>
                      dispatch({
                        type: 'SET_USER_LIMITS',
                        payload: {transactionType, limits},
                      })
                    }
                  />
                </Accordion>
              </div>

              {/* Business Update Section */}
              <div className="mt-6">
                <h3 className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 pb-2">
                  Business Configuration
                </h3>

                <Accordion
                  title="Current Processors"
                  headerStyle="bg-gray-200 dark:bg-gray-800 shadow-sm py-1.5"
                  onClick={(isActive) => setIsProcessorExpanded(isActive)}
                >
                  <BusinessTransactionProcessorConfig
                    business={state.business}
                    onUpdateProcessor={(transactionType, channel, processor) =>
                      dispatch({
                        type: 'UPDATE_BUSINESS_PROCESSOR',
                        payload: {transactionType, channel, processor},
                      })
                    }
                  />
                </Accordion>
              </div>
            </form>
          </div>

          {/* Fixed Button Footer */}
          <div className="flex w-full justify-end space-x-3 pt-4 px-3 border-t border-gray-200 dark:border-gray-700 dark:bg-dark-inputBg">
            <Button onClick={onClose} variant="outline" type="button">
              Cancel
            </Button>
            <Button
              disabled={state.isLoading}
              type="submit"
              form="currency-settings-form"
              variant="solid"
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UpdateFormModal;
